import React, { useState, useCallback } from 'react';
import { AlertCircle } from 'lucide-react';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import json from 'react-syntax-highlighter/dist/esm/languages/hljs/json';
import javascript from 'react-syntax-highlighter/dist/esm/languages/hljs/javascript';
import { githubGist } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { useMetadata } from '../utils/metadata';
import { CopyButton } from '../components/common/CopyButton';
import { DownloadButton } from '../components/common/DownloadButton';

SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('javascript', javascript);

function JsonConverter() {
  useMetadata({
    title: 'JavaScript Object to JSON Converter',
    description: 'Free online tool to convert JavaScript objects to valid JSON format. Format, validate, and beautify JSON with customizable indentation. Perfect for developers and data processing.',
    keywords: ['json converter', 'javascript to json', 'json formatter', 'json validator', 'json beautifier', 'object to json', 'code formatter']
  });
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [indentSize, setIndentSize] = useState(2);
  const [useTab, setUseTab] = useState(false);

  const convertToJson = useCallback((value: string, currentIndentSize: number, useTabIndent: boolean) => {
    if (!value.trim()) {
      setOutput('');
      setError('');
      return;
    }

    try {
      // Remove any trailing commas and wrap in parentheses to evaluate
      const cleanedInput = `(${value.replace(/,(\s*[}\]])/g, '$1')})`;
      // eslint-disable-next-line no-eval
      const obj = eval(cleanedInput);
      const indent = useTabIndent ? '\t' : ' '.repeat(currentIndentSize);
      setOutput(JSON.stringify(obj, null, indent));
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid input');
      setOutput('');
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInput(value);
    convertToJson(value, indentSize, useTab);
  };

  const handleIndentSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = Number(e.target.value);
    setIndentSize(newSize);
    convertToJson(input, newSize, useTab);
  };

  const handleUseTabChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newUseTab = e.target.checked;
    setUseTab(newUseTab);
    convertToJson(input, indentSize, newUseTab);
  };

  return (
    <div className="max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-4">JavaScript Object to JSON Converter</h1>
      <p className="text-gray-600 mb-8">
        Convert JavaScript objects to valid JSON format. Paste your JavaScript object in the left box,
        and see the converted JSON in the right box.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-medium text-gray-900">Input (JavaScript Object)</h2>
              <div className="flex space-x-2">
                <CopyButton text={input} />
                <DownloadButton content={input} filename="object.js" />
              </div>
            </div>
            <textarea
              value={input}
              onChange={handleInputChange}
              className="w-full h-96 font-mono text-sm p-4 border rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Paste your JavaScript object here..."
            />
          </div>
        </div>

        <div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-medium text-gray-900">Output (JSON)</h2>
              <div className="flex space-x-2">
                <CopyButton text={output} />
                <DownloadButton content={output} filename="output.json" />
              </div>
            </div>
            {error ? (
              <div className="flex items-center space-x-2 text-red-600 p-4 bg-red-50 rounded-md">
                <AlertCircle className="h-5 w-5" />
                <span>{error}</span>
              </div>
            ) : (
              <pre className="h-96 rounded-md bg-white p-4 overflow-x-auto" style={{ height: '24rem' }}>
                <code className="language-json whitespace-pre" data-testid="json-output">
                  {output}
                </code>
              </pre>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Converter Options</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">JSON indent spacing</label>
            <input
              type="number"
              min="0"
              max="8"
              value={indentSize}
              onChange={handleIndentSizeChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              disabled={useTab}
            />
          </div>
          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={useTab}
                onChange={handleUseTabChange}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm font-medium text-gray-700">Use tabs instead of spaces</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

export default JsonConverter;
